import { Ban, Check, Circle, Clock, Star, Undo2, X, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  applicationStatusColor,
  applicationStatusLabel,
  coverageStatusColor,
  coverageStatusLabel,
} from '@/shared/lib/labels';
import type { ApplicationStatus, CoverageStatus } from '@/shared/model/enums';

import { Badge } from './Badge';

/**
 * Status pills that pair color + glyph + label. The product rule (handoff
 * component sheet) is that a chip never conveys state by color alone — every
 * status maps to a distinct lucide glyph as well.
 */

const coverageGlyph: Record<CoverageStatus, LucideIcon> = {
  open: Circle,
  filled: Check,
  cancelled: X,
  expired: Clock,
};

const applicationGlyph: Record<ApplicationStatus, LucideIcon> = {
  pending: Clock,
  accepted: Check,
  rejected: X,
  withdrawn: Undo2,
  completed: Star,
  no_show: Ban,
};

export function CoverageStatusChip({ status }: { status: CoverageStatus }): JSX.Element {
  const { t } = useTranslation();
  const Icon = coverageGlyph[status];
  return (
    <Badge
      label={coverageStatusLabel(t, status)}
      color={coverageStatusColor(status)}
      icon={<Icon />}
    />
  );
}

export function ApplicationStatusChip({ status }: { status: ApplicationStatus }): JSX.Element {
  const { t } = useTranslation();
  const Icon = applicationGlyph[status];
  return (
    <Badge
      label={applicationStatusLabel(t, status)}
      color={applicationStatusColor(status)}
      icon={<Icon />}
    />
  );
}
